import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useAdminUpdateOrganizationFeatures,
  getAdminGetOrganizationQueryKey,
  useAdminGetFeatureTags,
} from '@/api/auth/admin/admin';
import type { AdminOrganizationResponse } from '@/api/auth/missionImpactAuthAPI.schemas';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '@/services/logger';

interface OrganizationFeaturesFormProps {
  organization: AdminOrganizationResponse;
}

/** A single configured feature row in the editor. */
interface FeatureRow {
  featureCode: string;
  value: string;
}

/**
 * Builds the ordered list of feature rows from the organization's current features.
 */
function toRows(organization: AdminOrganizationResponse): FeatureRow[] {
  return (organization.features ?? []).map((f) => ({
    featureCode: f.featureCode,
    value: f.value,
  }));
}

/**
 * Edits an organization's configured product features (feature code -> free-text value). Codes are
 * chosen from the feature catalog; values are free text. Saving replaces the whole feature map via
 * the admin features endpoint.
 */
export function OrganizationFeaturesForm({ organization }: OrganizationFeaturesFormProps) {
  const queryClient = useQueryClient();

  const { data: catalogData } = useAdminGetFeatureTags();
  const catalog = catalogData?.data?.featureTags || [];

  const [rows, setRows] = useState<FeatureRow[]>(() => toRows(organization));
  const [newCode, setNewCode] = useState('');
  const [newValue, setNewValue] = useState('');

  // Re-seed local rows from the server whenever the organization's features change
  // (for example after a successful save invalidates and refetches the organization).
  const serverKey = useMemo(
    () => JSON.stringify((organization.features ?? []).map((f) => [f.featureCode, f.value])),
    [organization.features]
  );
  useEffect(() => {
    setRows(toRows(organization));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverKey]);

  const { mutate, isPending } = useAdminUpdateOrganizationFeatures({
    mutation: {
      onSuccess: () => {
        toast.success('Organization features updated');
        queryClient.invalidateQueries({
          queryKey: getAdminGetOrganizationQueryKey(organization.id),
        });
      },
      onError: (error: any) => {
        logger.error(error, { context: 'UpdateOrganizationFeatures', orgId: organization.id });
        toast.error(
          'Failed to update features. ' + (error.response?.data?.detail || error.message)
        );
      },
    },
  });

  // Catalog codes not already configured on this organization, available to add.
  const availableCodes = catalog
    .filter((tag) => !rows.some((row) => row.featureCode === tag.featureCode))
    .map((tag) => tag.featureCode);

  const isDirty = serverKey !== JSON.stringify(rows.map((r) => [r.featureCode, r.value]));

  const updateRowValue = (featureCode: string, value: string) => {
    setRows((prev) => prev.map((r) => (r.featureCode === featureCode ? { ...r, value } : r)));
  };

  const removeRow = (featureCode: string) => {
    setRows((prev) => prev.filter((r) => r.featureCode !== featureCode));
  };

  const addRow = () => {
    if (!newCode) {
      return;
    }
    setRows((prev) => [...prev, { featureCode: newCode, value: newValue }]);
    setNewCode('');
    setNewValue('');
  };

  const save = () => {
    const features: Record<string, string> = {};
    for (const row of rows) {
      features[row.featureCode] = row.value;
    }
    mutate({ orgId: organization.id, data: { features } });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Product Features</CardTitle>
        <CardDescription>
          Configure product feature codes and their values for this organization. Consuming products
          interpret these codes and values.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No features configured for this organization.
          </p>
        ) : (
          <div className="space-y-2">
            {rows.map((row) => (
              <div key={row.featureCode} className="flex items-center gap-2">
                <code className="relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm flex-1 truncate">
                  {row.featureCode}
                </code>
                <Input
                  aria-label={`Value for ${row.featureCode}`}
                  className="flex-1"
                  value={row.value}
                  onChange={(e) => updateRowValue(row.featureCode, e.target.value)}
                  placeholder="value"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove ${row.featureCode}`}
                  onClick={() => removeRow(row.featureCode)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-end gap-2 border-t pt-4">
          <div className="grid gap-2 flex-1">
            <Label htmlFor="newFeatureCode">Add feature</Label>
            <Select
              value={newCode}
              onValueChange={setNewCode}
              disabled={availableCodes.length === 0}
            >
              <SelectTrigger id="newFeatureCode">
                <SelectValue
                  placeholder={
                    availableCodes.length === 0
                      ? 'No more catalog features'
                      : 'Select a feature code'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {availableCodes.map((code) => (
                  <SelectItem key={code} value={code}>
                    {code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Input
            aria-label="New feature value"
            className="flex-1"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            placeholder="value"
          />
          <Button variant="outline" onClick={addRow} disabled={!newCode}>
            Add
          </Button>
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={isPending || !isDirty}>
            {isPending ? 'Saving...' : 'Save Features'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
